import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Footer from "../components/Footer";
import Header from "../components/Header";
import SEO from "../components/SEO";
import { useAuthContext } from "../contexts/AuthContext";
import { rememberAuthReturn } from "../utils/conversionJourney";
import { premiumPlusPath } from "../utils/qbankCampaign";
import styles from "./MockExamDemo.module.css";
import { createDemoAttempt, gradeDemo, readDemoResult, saveDemoResult, clearDemoResult, DemoAttempt, DemoResults } from "../utils/publicQbankDemo";
import { trackTikTok } from "../utils/tiktok";
import { trackGa4 } from "../utils/analytics";

// public/index.html paints a static copy of the intro card on this route
// before the app bundle arrives; a tap there sets this flag.
const SHELL_START_FLAG = "__cshapedDemoStartRequested";

export const copy = {
    seoTitle: "Free SDLE Mini Mock Exam | C-Shaped",
    seoDescription: "Try 10 selected dental practice questions with a timer, answer explanations and source links.",
    eyebrow: "Free SDLE mini mock",
    title: "Try 10 SDLE practice questions.",
    intro: "Try the mock-exam experience before you subscribe. No account or payment is required.",
    questions: "questions",
    minutes: "minutes",
    noAccount: "no account",
    assurance: "A selected set of dental practice questions. Review the answers and sources after you submit.",
    start: "Start free mock",
    question: "Question",
    of: "of",
    previous: "Previous",
    next: "Next",
    submit: "Submit free mock",
    confirmTitle: "Submit your free mock?",
    confirmAnswered: "You answered",
    confirmUnanswered: "questions are still unanswered.",
    continueExam: "Continue exam",
    submitNow: "Submit now",
    answered: "answered",
    timeRemaining: "Time remaining",
    resultsEyebrow: "Your free mock result",
    resultsTitle: "Your practice results",
    correct: "correct",
    timeTaken: "Time taken",
    specialtySnapshot: "Specialty snapshot",
    review: "Question review",
    yourAnswer: "Your answer",
    correctAnswer: "Correct answer",
    skipped: "Skipped",
    explanation: "Explanation",
    references: "References",
    savedLocally: "This result is saved on this device for 24 hours.",
    signup: "Create a free account",
    unlock: "Unlock full mock exams",
    again: "Take another free mock",
};

function formatTime(seconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(safeSeconds / 60);
  const remainder = safeSeconds % 60;
  return `${minutes}:${remainder.toString().padStart(2, "0")}`;
}

const MockExamDemo: React.FC = () => {
  const location = useLocation();
  const { user } = useAuthContext();
  const text = copy;
  const returnUrl = new URL(`${location.pathname}${location.search}`, window.location.origin);
  returnUrl.searchParams.set("lang", "en");
  const returnPath = `${returnUrl.pathname}${returnUrl.search}`;
  const fullMockExamReturnPath = "/mock-exams?entry=sdle_sales&intent=mock_exam&lang=en";

  const [attempt, setAttempt] = useState<DemoAttempt | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(10 * 60);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [results, setResults] = useState<DemoResults | null>(readDemoResult);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);
  const completedAttempt = useRef<string | null>(null);
  const submitLatest = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (!attempt || !deadline || results) return;
    const tick = () => {
      const next = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemainingSeconds(next);
      if (next === 0) {
        submitLatest.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [attempt, deadline, results]);

  const startDemo = (source: "page" | "static_card" = "page") => {
    const next = createDemoAttempt();
    clearDemoResult();
    completedAttempt.current = null;
    setResults(null);
    setAttempt(next);
    setAnswers({});
    setCurrentIndex(0);
    setRemainingSeconds(next.duration_seconds);
    setDeadline(Date.now() + next.duration_seconds * 1000);
    trackTikTok("DemoStarted", "demo", `demo-started:${next.id}`);
    trackGa4("demo_start", { source });
  };

  // How long the app took to become usable on this device, and whether the
  // visitor already tapped Start on the static card while it loaded. A layout
  // effect starts the mock before the first paint, so the intro never flashes.
  useLayoutEffect(() => {
    const tappedStaticCard = (window as any)[SHELL_START_FLAG] === true;
    trackGa4("demo_landing_view", {
      load_ms: Math.round(performance.now()),
      static_card_tap: tappedStaticCard,
    }, { once: "demo_landing_view" });
    if (tappedStaticCard) {
      delete (window as any)[SHELL_START_FLAG];
      startDemo("static_card");
    }
    // Run once on arrival; startDemo only reads values that are fresh here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectAnswer = (questionId: string, optionId: string) => {
    setAnswers((current) => ({ ...current, [questionId]: optionId }));
    if (attempt) {
      trackGa4("demo_answer", { question_number: currentIndex + 1 }, { once: `demo_answer:${attempt.id}:${questionId}` });
    }
  };

  const submitDemo = () => {
    if (!attempt || completedAttempt.current === attempt.id) return;
    completedAttempt.current = attempt.id;
    const remaining = deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : 0;
    const elapsed = attempt.duration_seconds - remaining;
    const graded = gradeDemo(attempt.questions, answers, elapsed);
    setDeadline(null);
    setShowSubmitConfirmation(false);
    setResults(graded);
    saveDemoResult(answers, elapsed);
    trackTikTok("DemoCompleted", "demo", `demo-completed:${attempt.id}`);
    trackGa4("demo_complete", {
      score_percent: graded.score.percentage,
      answered: Object.keys(answers).length,
      seconds: elapsed,
    }, { once: `demo_complete:${attempt.id}` });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  submitLatest.current = submitDemo;

  const requestSubmit = () => {
    if (!attempt) return;
    setShowSubmitConfirmation(true);
  };

  const resetDemo = () => {
    setAttempt(null);
    setAnswers({});
    setCurrentIndex(0);
    setRemainingSeconds(10 * 60);
    setDeadline(null);
    setResults(null);
    setShowSubmitConfirmation(false);
    clearDemoResult();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderResults = () => {
    if (!results) return null;
    return (
      <main className={styles.resultsMain}>
        <section className={styles.resultsHero}>
          <p className={styles.eyebrow}>{text.resultsEyebrow}</p>
          <h1>{text.resultsTitle}</h1>
          <div className={styles.score}>{results.score.percentage}%</div>
          <p className={styles.scoreLabel}>
            {results.score.correct}/{results.score.total} {text.correct}
          </p>
          <p className={styles.savedNote}>{text.savedLocally}</p>
        </section>

        <section className={styles.summaryGrid} aria-label={text.specialtySnapshot}>
          <article className={styles.timeCard}>
            <span>{text.timeTaken}</span>
            <strong>{formatTime(results.time_taken_seconds)}</strong>
          </article>
          {results.specialty_breakdown.map((specialty) => (
            <article key={specialty.specialty} className={styles.specialtyCard}>
              <span>{specialty.specialty}</span>
              <strong>{specialty.correct}/{specialty.total}</strong>
            </article>
          ))}
        </section>

        <section className={styles.reviewSection}>
          <h2>{text.review}</h2>
          <div className={styles.reviewList}>
            {results.questions.map((question, index) => (
              <article
                key={question.id}
                className={`${styles.reviewCard} ${question.is_correct ? styles.reviewCorrect : styles.reviewIncorrect}`}
              >
                <div className={styles.reviewMeta}>
                  <strong>{text.question} {index + 1}</strong>
                  <span>{question.specialty}</span>
                </div>
                <p className={styles.reviewQuestion}>{question.question_text}</p>
                <div className={styles.answerSummary}>
                  <span>{text.yourAnswer}: <strong>{question.user_answer
                    ? `${question.user_answer}. ${question.options.find(option => option.label === question.user_answer)?.text}`
                    : text.skipped}</strong></span>
                  <span>{text.correctAnswer}: <strong>{question.correct_answer}. {question.options.find(option => option.id === question.correct_option_id)?.text}</strong></span>
                </div>
                {question.explanation && (
                  <div className={styles.explanation}>
                    <h3>{text.explanation}</h3>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{question.explanation}</ReactMarkdown>
                  </div>
                )}
                {question.references?.length > 0 && (
                  <div className={styles.references}>
                    <h3>{text.references}</h3>
                    <ul>
                      {question.references.map((reference, referenceIndex) => (
                        <li key={`${question.id}-${referenceIndex}`}><a href={reference.url} target="_blank" rel="noreferrer">{reference.title}</a></li>
                      ))}
                    </ul>
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>

        <section className={styles.resultActions}>
          {!user && (
            <Link
              to="/signup"
              state={{ from: returnPath }}
              className={styles.primaryButton}
              onClick={() => {
                rememberAuthReturn(returnPath);
                trackGa4("demo_cta", { cta: "signup" });
              }}
            >
              {text.signup}
            </Link>
          )}
          <Link
            to={premiumPlusPath(fullMockExamReturnPath)}
            className={user ? styles.primaryButton : styles.secondaryButton}
            onClick={() => trackGa4("demo_cta", { cta: "unlock" })}
          >
            {text.unlock}
          </Link>
          <button type="button" className={styles.textButton} onClick={() => { trackGa4("demo_cta", { cta: "again" }); resetDemo(); }}>
            {text.again}
          </button>
        </section>
      </main>
    );
  };

  const renderExam = () => {
    if (!attempt) return null;
    const question = attempt.questions[currentIndex];
    const answeredCount = Object.keys(answers).length;
    return (
      <main className={styles.examMain}>
        <section className={styles.questionCard}>
          <div className={styles.questionHeader}>
            <div>
              <span>{text.question}</span>
              <strong>{currentIndex + 1} {text.of} {attempt.total_questions}</strong>
            </div>
            <span className={styles.specialty}>{question.specialty}</span>
          </div>
          <h1 className={styles.questionText}>{question.question_text}</h1>
          <div className={styles.options}>
            {question.options.map((option) => (
              <button
                key={option.label}
                type="button"
                className={`${styles.option} ${answers[question.id] === option.id ? styles.optionSelected : ""}`}
                aria-pressed={answers[question.id] === option.id}
                onClick={() => selectAnswer(question.id, option.id)}
              >
                <span className={styles.optionLabel}>{option.label}</span>
                <span>{option.text}</span>
              </button>
            ))}
          </div>
          <div className={styles.navigation}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={currentIndex === 0}
              onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
            >
              {text.previous}
            </button>
            {currentIndex < attempt.questions.length - 1 ? (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={() => setCurrentIndex((index) => Math.min(attempt.questions.length - 1, index + 1))}
              >
                {text.next}
              </button>
            ) : (
              <button
                type="button"
                className={styles.primaryButton}
                onClick={requestSubmit}
              >
                {text.submit}
              </button>
            )}
          </div>
        </section>

        <aside className={styles.examSidebar}>
          <div className={styles.timerCard}>
            <span>{text.timeRemaining}</span>
            <strong>{formatTime(remainingSeconds)}</strong>
          </div>
          <div className={styles.progressCard}>
            <span>{answeredCount}/{attempt.total_questions} {text.answered}</span>
            <div className={styles.progressTrack}>
              <span style={{ width: `${(answeredCount / attempt.total_questions) * 100}%` }} />
            </div>
            <div className={styles.questionGrid}>
              {attempt.questions.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.gridButton} ${index === currentIndex ? styles.gridCurrent : ""} ${answers[item.id] ? styles.gridAnswered : ""}`}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`${text.question} ${index + 1}`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className={styles.submitAllButton}
            onClick={requestSubmit}
          >
            {text.submit}
          </button>
        </aside>
        {showSubmitConfirmation && (
          <div className={styles.modalOverlay}>
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="demo-submit-title">
              <h2 id="demo-submit-title">{text.confirmTitle}</h2>
              <p>
                {text.confirmAnswered} {answeredCount} {text.of} {attempt.total_questions}.<br />
                <strong>{attempt.total_questions - answeredCount}</strong> {text.confirmUnanswered}
              </p>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.secondaryButton}
                  onClick={() => setShowSubmitConfirmation(false)}
                >
                  {text.continueExam}
                </button>
                <button
                  type="button"
                  className={styles.primaryButton}
                  onClick={() => {
                    setShowSubmitConfirmation(false);
                    submitDemo();
                  }}
                >
                  {text.submitNow}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    );
  };

  return (
    <div className={styles.page} dir="ltr" lang="en">
      <SEO title={text.seoTitle} description={text.seoDescription} canonical="https://c-shaped.com/mock-exams/demo" />
      <Header language="en" />
      {results ? renderResults() : attempt ? renderExam() : (
        <main className={styles.introMain}>
          <section className={styles.introCard}>
            <p className={styles.eyebrow}>{text.eyebrow}</p>
            <h1>{text.title}</h1>
            <p className={styles.introCopy}>{text.intro}</p>
            <div className={styles.facts}>
              <div><strong>10</strong><span>{text.questions}</span></div>
              <div><strong>10</strong><span>{text.minutes}</span></div>
              <div><strong>0</strong><span>{text.noAccount}</span></div>
            </div>
            <p className={styles.assurance}>{text.assurance}</p>
            <button type="button" className={styles.startButton} onClick={() => startDemo()}>
              {text.start}
            </button>
          </section>
        </main>
      )}
      <Footer language="en" />
    </div>
  );
};

export default MockExamDemo;
